import type { SerializedError } from '@reduxjs/toolkit';
import type { FetchBaseQueryError } from '@reduxjs/toolkit/query';

export type ValidationErrors = Record<
  string,
  Array<{ message: string; code: string | null }>
>;

export type ProblemDetailsData = {
  type?: string | null;
  title?: string | null;
  status?: number | null;
  detail?: string | null;
  instance?: string | null;
  errorCode?: string | null;
  errors?: ValidationErrors;
  [key: string]: unknown;
};

export function isFetchBaseQueryError(
  error: unknown,
): error is FetchBaseQueryError {
  return typeof error === 'object' && error !== null && 'status' in error;
}

export function extractProblemDetails(
  error: FetchBaseQueryError,
): ProblemDetailsData | null {
  if (typeof error.data === 'object' && error.data !== null) {
    return error.data as ProblemDetailsData;
  }
  return null;
}

export function extractHttpStatus(error: FetchBaseQueryError): number | null {
  return typeof error.status === 'number' ? error.status : null;
}

/**
 * Extracts the backend error code from ProblemDetails.
 *
 * Checks two locations (in order):
 * 1. A dedicated `errorCode` extension field, e.g. `{ errorCode: "400.202" }`
 * 2. A bracketed prefix in the `detail` field, e.g. `"[400.202] The property..."`
 */
export function extractErrorCode(
  problemDetails: ProblemDetailsData,
): string | null {
  if (
    problemDetails.errorCode &&
    typeof problemDetails.errorCode === 'string'
  ) {
    return problemDetails.errorCode;
  }

  if (typeof problemDetails.detail === 'string') {
    const match = problemDetails.detail.match(/^\[(\d+\.\d+)\]/);
    if (match) return match[1];
  }

  return null;
}

/**
 * Normalizes a backend error code to a safe i18next key.
 * Replaces dots with underscores: "400.202" -> "400_202"
 */
export function normalizeErrorCode(code: string): string {
  return code.replace(/\./g, '_');
}

/**
 * Returns the appropriate snackbar severity for a given HTTP status.
 * 4xx errors are user-caused (warning), 5xx and network errors are server-caused (error).
 */
export function getErrorSeverity(status: number | null): 'warning' | 'error' {
  switch (status) {
    case 400:
    case 409:
    case 422:
    case 429:
      return 'warning';

    default:
      return 'error';
  }
}

/**
 * Returns true if the ProblemDetails represents standard ASP.NET Core
 * model validation errors (the `errors` map with field-level messages).
 */
export function isValidationError(problemDetails: ProblemDetailsData): boolean {
  return (
    typeof problemDetails.errors === 'object' &&
    problemDetails.errors !== null &&
    !Array.isArray(problemDetails.errors)
  );
}

export type ApiError = FetchBaseQueryError | SerializedError;
